/**
 * AutoLogout - Automatic logout after inactivity
 * 
 * Tracks user activity across all browser tabs and automatically logs out
 * after a specified period of inactivity.
 * 
 * Features:
 * - Tracks mouse, keyboard, touch, and scroll events
 * - Syncs activity across browser tabs using BroadcastChannel
 * - Configurable timeout period
 * - Automatic cleanup on logout
 * 
 * Usage:
 * ```typescript
 * const autoLogout = new AutoLogout(
 *   () => { window.location.href = '/logout'; },
 *   30 // 30 minutes
 * );
 * ```
 */

export default class AutoLogout {
  private timeoutId: number | undefined;
  private inactivityTimeMinutes: number;
  private events: string[];
  private channel: BroadcastChannel;
  private logoutCallback: Function;
  private isActive: boolean;
  private activityHandler: () => void;

  constructor(logoutCallback: Function, inactivityTimeMinutes: number = 30) {
    this.logoutCallback = logoutCallback;
    this.inactivityTimeMinutes = inactivityTimeMinutes;
    this.events = ['mousemove', 'mousedown', 'keypress', 'touchstart', 'scroll'];
    this.channel = new BroadcastChannel('lender-user-activity');
    this.isActive = true;
    this.activityHandler = () => this.handleActivity();
    this.init();

    if (import.meta.env.DEV) console.log(`🔒 [AutoLogout] Initialized with ${inactivityTimeMinutes} minute timeout`);
  }

  private init(): void {
    this.resetTimer();
    this.events.forEach(event => {
      window.addEventListener(event, this.activityHandler);
    });
    this.channel.addEventListener('message', (event) => this.handleChannelMessage(event));
  }

  private handleActivity(): void {
    try {
      this.channel.postMessage({ type: 'activity', timestamp: Date.now() });
    } catch (error) {
      // Channel might be closed, ignore the error
      // This can happen during cleanup or when the component is unmounting
    }
    this.resetTimer();
  }

  private handleChannelMessage(event: MessageEvent): void {
    if (event.data && event.data.type === 'activity') {
      this.resetTimer();
    }
  }

  private resetTimer(): void {
    this.isActive = true;
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
    }
    this.timeoutId = window.setTimeout(
      () => this.handleInactivity(),
      this.inactivityTimeMinutes * 1000 * 60
    );
  }

  private handleInactivity(): void {
    if (this.isActive === true) {
      this.isActive = false;
      if (import.meta.env.DEV) console.warn(`⏱️ [AutoLogout] Session timed out after ${this.inactivityTimeMinutes} minutes of inactivity`);
      this.logoutCallback();
    }
  }

  /**
   * Cleanup method to remove event listeners and close channel
   */
  public destroy(): void {
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
    }
    this.events.forEach(event => {
      window.removeEventListener(event, this.activityHandler);
    });
    try {
      this.channel.close();
    } catch (error) {
      // Channel might already be closed, ignore the error
    }
    if (import.meta.env.DEV) console.log('🔒 [AutoLogout] Destroyed');
  }
}

